import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentSends, DocumentSend } from '@/hooks/useDocumentSends';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import EmailStatusBadge from './EmailStatusBadge';
import EmailBodyPreview from './EmailBodyPreview';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, RefreshCw, ChevronDown, Paperclip, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQueryClient } from '@tanstack/react-query';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';

interface DocumentSendHistoryProps {
  documentType: 'estimate' | 'invoice' | 'change_order';
  documentId: string;
  customerId?: string;
  compact?: boolean;
}

const DocumentSendHistory: React.FC<DocumentSendHistoryProps> = ({
  documentType,
  documentId,
  customerId,
  compact = false,
}) => {
  const { data: sends, isLoading, refetch } = useDocumentSends(documentType, documentId);
  const queryClient = useQueryClient();
  const [isRefetching, setIsRefetching] = useState(false);

  const handleRefresh = async () => {
    setIsRefetching(true);
    try {
      await refetch();
    } finally {
      setIsRefetching(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (!sends || sends.length === 0) {
    return (
      <div className="text-center py-4 text-muted-foreground">
        <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No emails sent yet</p>
      </div>
    );
  }

  if (compact) {
    const latestSend = sends[0];
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">
          Last sent: {format(new Date(latestSend.sent_at), 'dd MMMM yyyy')}
        </span>
        <EmailStatusBadge 
          status={latestSend.status}
          sentAt={latestSend.sent_at}
          deliveredAt={latestSend.delivered_at}
          openedAt={latestSend.opened_at}
          clickedAt={latestSend.clicked_at}
          bouncedAt={latestSend.bounced_at}
          openCount={latestSend.open_count}
          clickCount={latestSend.click_count}
          bounceReason={latestSend.bounce_reason}
        />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Email History
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={handleRefresh} disabled={isRefetching}>
            <RefreshCw className={cn("h-4 w-4", isRefetching && "animate-spin")} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {sends.map((send) => (
            <SendHistoryItem key={send.id} send={send} customerId={customerId} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

const SendHistoryItem: React.FC<{ send: DocumentSend; customerId?: string }> = ({ send, customerId }) => {
  const [expanded, setExpanded] = useState(false);

  // Fetch version details if version_id exists
  const { data: versionInfo } = useQuery({
    queryKey: ['version-info', send.version_id],
    queryFn: async () => {
      if (!send.version_id) return null;
      const { data } = await supabase
        .from('estimate_versions')
        .select('version_number, version_type')
        .eq('id', send.version_id)
        .single();
      return data;
    },
    enabled: !!send.version_id,
  });

  const getVersionLabel = () => {
    if (!versionInfo) return null;
    if (versionInfo.version_type === 'change_order') {
      return `Change Order v${versionInfo.version_number}`;
    }
    return `Version ${versionInfo.version_number}`;
  };

  const getVersionBadgeLabel = () => {
    if (!versionInfo) return null;
    if (versionInfo.version_type === 'change_order') {
      return `CO v${versionInfo.version_number}`;
    }
    return `v${versionInfo.version_number}`;
  };

  return (
    <Collapsible open={expanded} onOpenChange={setExpanded}>
      <div className="p-3 bg-muted/50 rounded-lg overflow-hidden">
        <CollapsibleTrigger className="w-full text-left">
          <div className="flex items-start justify-between gap-2 min-w-0">
            <div className="space-y-1 min-w-0 overflow-hidden flex-1">
              <div className="font-medium text-sm flex items-center gap-2 min-w-0">
                <span className="truncate">{send.recipient_email}</span>
                {versionInfo && (
                  <Badge variant="outline" className="text-xs shrink-0">
                    {getVersionBadgeLabel()}
                  </Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                {format(new Date(send.sent_at), 'dd MMMM yyyy HH:mm')}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {send.subject}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0 ml-2 mr-2"> 
              <EmailStatusBadge 
                status={send.status}
                sentAt={send.sent_at}
                deliveredAt={send.delivered_at}
                openedAt={send.opened_at}
                clickedAt={send.clicked_at}
                bouncedAt={send.bounced_at}
                openCount={send.open_count}
                clickCount={send.click_count}
                bounceReason={send.bounce_reason}
                showDetails
              />
              <ChevronDown className={cn("h-4 w-4 transition-transform", expanded && "rotate-180")} />
            </div>
          </div>
        </CollapsibleTrigger>
        
        <CollapsibleContent className="pt-3 mt-3 border-t border-border">
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Subject</Label>
              <p className="text-sm mt-1">{send.subject}</p>
            </div>
            
            <div>
              <Label className="text-xs text-muted-foreground">Email Body Preview</Label>
              <div className="mt-1">
                <EmailBodyPreview html={send.body_html} height={200} />
              </div>
            </div>
            
            {versionInfo && (
              <div>
                <Label className="text-xs text-muted-foreground">Attachment</Label>
                <div className="flex items-center gap-2 mt-1 text-sm">
                  <Paperclip className="h-4 w-4 text-muted-foreground" />
                  <span>{getVersionLabel()} (PDF)</span>
                </div>
              </div>
            )}

            {customerId && (
              <div className="pt-2 border-t border-border">
                <Link 
                  to={`/customers?id=${customerId}&tab=emails#email-${send.id}`}
                  className="text-sm text-primary hover:underline inline-flex items-center gap-1.5"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  View full email on customer page
                </Link>
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};

export default DocumentSendHistory;
